<template>
    <div component="VideoGallery">
        <page-header :icon="'smart_display'" :text="'My Videos'" />
        <section container="detail">
            <div class="buttons">
                <a
                    class="button camera"
                    :state="getShowCamera ? 'load' : ''"
                    size=""
                    color="accent"
                    @click="$_eventsDispatch('openVideoPortfolioRecorder')"
                >
                    <i class="icon">camera_alt</i><span>Record</span>
                </a>
                <video-gallery-upload :uploading="[states.uploading].includes(state)" @submit="onUpload" />
            </div>
            <section container="list">
                <save-progress-indicator :state="state" />
                <div v-bar class="vb">
                    <div>
                        <draggable class="draggable my-videos" v-model="videos" @end="reorder" handle=".drag">
                            <video-gallery-item
                                class="thumbnail"
                                v-for="(item, index) in videos"
                                :key="item.id"
                                :item="item"
                                :index="index"
                                @click="play"
                                @remove="remove"
                            />
                        </draggable>
                    </div>
                </div>
            </section>
        </section>
    </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex';
import draggable from 'vuedraggable';
import PageHeader from '@/components/Profile/ProfileDetailHeader.vue';
import VideoGalleryItem from './VideoGalleryItem.vue';
import SaveProgressIndicator from '@/components/.global/SaveProgressIndicator.vue';
import VideoGalleryUpload from './VideoGalleryUpload.vue';
import { ActorsApi } from '@/api';
import { ActorPortfolioModel } from '@/model';

const states = {
    idle: 'idle',
    saving: 'saving',
    saved: 'saved',
    uploading: 'uploading',
    deleting: 'deleting',
    error: 'error',
};

export default {
    name: 'VideoGallery',

    components: {
        VideoGalleryUpload,
        VideoGalleryItem,
        PageHeader,
        draggable,
        SaveProgressIndicator,
    },

    data() {
        return {
            states,
            state: states.idle,
            error: null,
            videos: [], // Main list
            poll: null,
        };
    },

    created() {
        this.$_eventsSubscribe({ videoPortfolioItemCreated: 'startPolling' });
    },

    mounted() {
        this.readPortfolioVideos();
        if (this.hasUnprocessedVideos) {
            this.startPolling();
        }
    },

    beforeDestroy() {
        this.$_eventsUnsubscribe();
        this.stopPolling();
    },

    methods: {
        ...mapMutations(['setActorVideoPlaying']),

        readPortfolioVideos() {
            this.videos = this.getActor.portfolio.filter((item) => item.file_type == 'video');
        },

        async reorder() {
            if (this.$_stateIs(states.saving)) {
                return;
            }

            this.videos.forEach((v, i) => {
                v.poradie = i + 1;
            });

            this.getActor.portfolio.forEach((i) => {
                const found = this.videos.find((v) => v.id == i.id);
                if (found) {
                    i.poradie = found.poradie;
                }
            });

            this.$_setState(states.saving);

            try {
                await this.getActor.savePortfolio();
                this.$_setState(states.saved);
            } catch (e) {
                this.$env.log.error(e);
                this.$_setState(states.error);
            } finally {
                this.$_setState(null, states.idle);
            }
        },

        play(file_id) {
            this.setActorVideoPlaying({
                file: this.getActorFileUrl(file_id),
                controls: true,
                loop: false,
            });
        },

        async remove(item) {
            this.getActor.removePortfolioItem(item);
            this.readPortfolioVideos();
            this.$_setState(states.deleting);
            try {
                await ActorsApi.removeActorPortfolio(item);
                this.$_setState(states.deleting, states.idle);
            } catch (e) {
                this.$_setState(states.error, states.idle);
                throw e;
            }
        },

        async onUpload(file) {
            this.$_setState(states.uploading);
            try {
                const { data } = await ActorsApi.createActorPortfolio(
                    this.getActor,
                    {
                        file_type: 'video',
                        nazov: file.name.split('.')[0],
                        poradie: 0,
                    },
                    file
                );

                const record = new ActorPortfolioModel(data);

                this.getActor.addPortfolioItem(record);
                this.readPortfolioVideos();
                await this.reorder();
                this.$_eventsDispatch('videoPortfolioItemCreated', record);
            } catch (e) {
                this.$_setState(states.error, states.idle);
                this.$env.log.error(e);
                this.$bugsnag.notify(e);
            }
        },

        startPolling() {
            const poll = async () => {
                if (!this.hasUnprocessedVideos) {
                    return;
                }
                await this.getActor.load('portfolio');
                this.readPortfolioVideos();
                this.poll = setTimeout(poll, 5000);
            };

            this.readPortfolioVideos();

            if (this.hasUnprocessedVideos) {
                this.poll = setTimeout(poll, 1000);
            }
        },

        stopPolling() {
            if (this.poll) {
                clearTimeout(this.poll);
            }
        },
    },

    computed: {
        ...mapGetters(['getActor', 'getShowCamera', 'getActorFileUrl']),

        hasUnprocessedVideos() {
            return this.videos.some((i) => i.state != 'ready');
        },
    },
};
</script>
<style lang="less" scoped>
@import '~@variables';

[component='VideoGallery'] {
    display: flex;
    flex-direction: column;
    height: 100%;

    [container='list'] {
        overflow-y: auto;
        align-items: flex-start;
        justify-content: flex-start;
        padding: 10px 0 0 0;
        height: 0;
        flex-grow: 1;

        .vb {
            width: 100%;
        }
    }

    .my-videos {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: flex-start;
        padding-right: 15px;
        overflow-y: auto;
    }

    #photoFile {
        display: none;
    }

    .buttons {
        width: 100%;
        display: flex;
        z-index: 40;

        .camera {
            margin-right: 5px;

            &.disabled {
                background: grey;

                &:hover::before {
                    opacity: 0;
                }
            }
        }
    }

    .button.save {
        width: ~'calc(100% - 15px)';
        margin-top: 20px;
    }
}
</style>
